import { Button } from '@/components/Button'
import {
  NEW_YEAR_THEME,
  YANDEX_METRIKA,
  YANDEX_METRIKA_COUNTER,
} from '@/config'
import { useScrollHandler } from '@/hooks/scrollHandler'
import { Icon } from '@/icons'
import { navigationMenuStore } from '@/store/NavigationMenu'
import { orderCallPopupStore } from '@/store/OrderCallPopup.store'
import { disableBodyScroll, enableBodyScroll } from '@/utils/DOM'
import cn from 'classnames'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { AccountButton } from '../AccountButton'
import { MobileMenu } from '../MobileMenu'
import { Navigation } from '../Navigation'
import styles from './Header.module.scss'

const Garland = dynamic(
  () => import('@/modules/NewYearElements/Garland').then((m) => m.Garland),
  { ssr: false }
)

enum STATUS_POSITION_HEADER {
  DEFAULT = 'DEFAULT',
  FIXED = 'FIXED',
  UNFIXED = 'UNFIXED',
}

interface HeaderProps {
  telegram: string
  whatsapp: string
  max: string
}
export const Header: FC<HeaderProps> = ({ telegram, whatsapp, max }) => {
  const [visibleNavigationMenu, setVisibleNavigationMenu] =
    useState<boolean>(false)

  const [visibleMenu, setVisibleMenu] = useState<boolean>(false)
  const [positionHeader, setPositionHeader] = useState(
    STATUS_POSITION_HEADER.DEFAULT
  )
  const pathname = usePathname()
  const cachePathname = useRef<string | null>(null)

  const toggleMenu = useCallback(() => {
    if (!visibleMenu) {
      disableBodyScroll()
    } else {
      enableBodyScroll()
    }
    setVisibleMenu((status) => !status)
  }, [visibleMenu])

  const hideMenu = useCallback(() => {
    enableBodyScroll()
    setVisibleMenu(false)
  }, [])

  const toggleNavigationMenu = useCallback(() => {
    setVisibleNavigationMenu((status) => !status)
  }, [])

  const orderCallClick = useCallback(() => {
    if (YANDEX_METRIKA) {
      // @ts-ignore
      ym(YANDEX_METRIKA_COUNTER, 'reachGoal', 'BUTTON_ORDER_CALL_CLICK')
    }
    orderCallPopupStore.show()
  }, [])

  const fixedHeaderToggle = useCallback(() => {
    if (
      (positionHeader === STATUS_POSITION_HEADER.UNFIXED ||
        positionHeader === STATUS_POSITION_HEADER.DEFAULT) &&
      window.scrollY >= 90
    ) {
      setPositionHeader(STATUS_POSITION_HEADER.FIXED)
    }

    if (
      positionHeader === STATUS_POSITION_HEADER.FIXED &&
      window.scrollY === 0
    ) {
      setPositionHeader(STATUS_POSITION_HEADER.UNFIXED)
    }
  }, [positionHeader])

  useEffect(() => {
    if (cachePathname.current != null && cachePathname.current != pathname) {
      hideMenu()
    }
    if (cachePathname.current != pathname) {
      cachePathname.current = pathname
    }
  }, [pathname, hideMenu])

  useLayoutEffect(() => {
    navigationMenuStore.getArticle()
  }, [])

  useEffect(() => {
    fixedHeaderToggle()
  }, [fixedHeaderToggle])

  useScrollHandler(fixedHeaderToggle)

  return (
    <>
      <header
        className={cn(styles.header, {
          [styles.isNavigationOpen]: visibleNavigationMenu || visibleMenu,
          [styles.fixed]: positionHeader === STATUS_POSITION_HEADER.FIXED,
          [styles.unfixed]: positionHeader === STATUS_POSITION_HEADER.UNFIXED,
        })}
      >
        {NEW_YEAR_THEME ? <Garland /> : null}

        <div className={styles.headerLeft}>
          <Link
            href="/"
            className={styles.headerLogoLink}
            aria-label="главная страница"
          >
            {NEW_YEAR_THEME ? (
              <Icon name="logoNewYear" className={styles.headerLogo} />
            ) : (
              <Icon name="logo" className={styles.headerLogo} />
            )}
          </Link>
        </div>

        <div className={styles.headerCenter}>
          <Navigation
            toggleNavigationMenu={toggleNavigationMenu}
            className={styles.navigation}
            telegram={telegram}
            whatsapp={whatsapp}
            max={max}
          />
        </div>

        <div className={styles.headerRight}>
          <a href="tel:88002220403" className={styles.headerPhone}>
            8 (800) 222-04-03
          </a>
          <div className={styles.headerButtons}>
            <Button
              className={styles.callBtn}
              variant={'opacity'}
              type="button"
              onClick={orderCallClick}
            >
              Обратный звонок
            </Button>
            <AccountButton />
            <Button
              variant="opacity"
              className={styles.mobileMenuButton}
              onClick={toggleMenu}
              ariaLabel="Меню"
            >
              {visibleMenu ? (
                <Icon name="close" className={styles.iconCloseBurger} />
              ) : (
                <Icon name="burger" />
              )}
            </Button>
          </div>
        </div>
      </header>

      {visibleMenu ? (
        <MobileMenu
          closeMenu={hideMenu}
          className={cn(styles.mobileMenu, {
            [styles.isVisible]: visibleMenu,
          })}
          whatsapp={whatsapp}
          telegram={telegram}
          max={max}
        />
      ) : null}
    </>
  )
}
